import React, { memo, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { UncontrolledAlert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons';

import { Width } from '../../Context/WidthContext';
import { MAINTENANCE_LOG_ID } from '../../Constants/StaticUUIDs';
import ServiceTableMobileFilters from '../Maintenance/ServiceTableMobileFilters';
import ServiceCompleteModalContainer from '../Maintenance/ServiceCompleteModalContainer';
import MaintenanceTable from './AssetMaintenanceTable';
import MobileMaintenanceCard from './MobileMaintenanceCard';
import MobileMaintenanceFilters from './MobileMaintenanceFilters';
import LoadingCircle from '../LoadingCircle';
import { NoItemsFound } from '../NoDataIndication';
import PaginationFooter from '../PaginationFooter';
import { STICKY_OVERRIDE } from '../../Styles/Z-INDEX';

export default memo(AssetMaintenanceDisplay);

const pageLimit = 12;

function AssetMaintenanceDisplay(props) {
  const width = useContext(Width);
  const [showMobileFilters, toggleMobileFilters] = useState(false);
  const filteredData = useMemo(() => {
    const parsedInstances = props.assetReminderInstances.reduce(
      (acc, { triggerHistory = [], ...instance }) => {
        triggerHistory.forEach(({ _id, ...history } = {}) => {
          const mapValue = { ...instance, ...history };
          switch (true) {
            case !!history.maintenanceLogId &&
              history.maintenanceLogId !== MAINTENANCE_LOG_ID:
              acc.complete.set(_id, mapValue);
              break;
            case !history.due:
              acc.upcoming.set(_id, mapValue);
              break;
            case (history.due && !history.maintenanceLogId) ||
              history.maintenanceLogId === MAINTENANCE_LOG_ID:
              acc.due.set(_id, mapValue);
          }
        });
        return acc;
      },
      { complete: new Map(), due: new Map(), upcoming: new Map() }
    );

    return props.typeFilter
      .reduce((acc, type) => {
        if (parsedInstances[type])
          acc.push(...Array.from(parsedInstances[type]));
        return acc;
      }, [])
      .filter(
        ([, { reminder: { name: reminderName } = {}, scheduleName = '' }]) => {
          if (!props.reminderNameSearch && !props.scheduleNameSearch) {
            return true;
          }

          const prunedReminderName = reminderName.trim().toLowerCase();
          const prunedScheduleName = scheduleName.trim().toLowerCase();

          switch (true) {
            case !!props.reminderNameSearch && !!props.scheduleNameSearch: {
              const prunedReminderSearch = props.reminderNameSearch
                .trim()
                .toLowerCase();
              const prunedScheduleSearch = props.scheduleNameSearch
                .trim()
                .toLowerCase();
              return (
                prunedReminderName.includes(prunedReminderSearch) &&
                prunedScheduleName.includes(prunedScheduleSearch)
              );
            }
            case !!props.reminderNameSearch: {
              const prunedReminderSearch = props.reminderNameSearch
                .trim()
                .toLowerCase();
              return prunedReminderName.includes(prunedReminderSearch);
            }
            case !!props.scheduleNameSearch: {
              const prunedScheduleSearch = props.scheduleNameSearch
                .trim()
                .toLowerCase();
              return prunedScheduleName.includes(prunedScheduleSearch);
            }
            default:
              return false;
          }
        }
      )
      .sort(([, { createdAt: a }], [, { createdAt: b }]) => {
        const dateA = new Date(a).valueOf();
        const dateB = new Date(b).valueOf();
        switch (true) {
          case dateA > dateB:
            return -1;
          case dateA < dateB:
            return 1;
          default:
            return 0;
        }
      });
  }, [
    props.assetReminderInstances,
    props.reminderNameSearch,
    props.scheduleNameSearch,
    props.typeFilter,
  ]);

  const upcomingAndDue = filteredData.filter(
    ([, { due, maintenanceLogId } = {}]) =>
      (due && !maintenanceLogId) || maintenanceLogId === MAINTENANCE_LOG_ID
  );

  const handleAllRowsChecked = () => {
    if (!upcomingAndDue.length) return;
    const allChecked =
      upcomingAndDue.length &&
      props.checkedInstances.size === upcomingAndDue.length;
    props.handleSelectAllInstances(upcomingAndDue, allChecked);
  };

  const pages = Math.ceil(filteredData.length / pageLimit);

  return (
    <>
      <button
        className="d-lg-none btn btn-link p-0 shadow-none text-decoration-none position-absolute"
        onClick={() => toggleMobileFilters(!showMobileFilters)}
        style={{ right: '0.5rem', top: '-5.7rem', zIndex: STICKY_OVERRIDE }}
      >
        <FontAwesomeIcon
          className="text-brand-primary"
          icon={faSlidersH}
          size="lg"
        />
      </button>
      <div className="position-absolute h-100 w-100 bg-white rounded-lg d-flex flex-column p-1 p-lg-3 px-lg-5">
        <UncontrolledAlert className="text-white text-center" color="danger">
          This Maintenance feature is deprecated and will be removed in December
          2023. Please migrate to using Maintenance Reminders in Asset Model.
        </UncontrolledAlert>
        <div className="flex-fill d-flex flex-column">
          <div className="w-100 d-flex align-items-center my-2 my-lg-3">
            <button
              className="btn modal-button-primary three-quarter-text"
              onClick={() =>
                props.toggleServiceCompleteModal(
                  !props.showServiceCompleteModal
                )
              }
              disabled={props.dataLoading || !props.checkedInstances.size}
            >
              Complete Maintenance
            </button>
            <p className="nine-tenth-text text-icon-link-gray mb-0 mx-2 rounded">
              {props.checkedInstances.size} selected
            </p>
          </div>
          {upcomingAndDue.length ? (
            <button
              className="d-lg-none btn btn-block btn-outline-warning text-warning bg-white py-0 w-75 align-self-center"
              onClick={handleAllRowsChecked}
            >
              Select All Reminders
            </button>
          ) : null}
          {props.error ? (
            <p className="w-100 mb-0 nine-tenth-text text-danger">
              {props.error}
            </p>
          ) : null}
          <div className="flex-fill mt-2 position-relative">
            {(() => {
              switch (true) {
                case props.dataLoading:
                  return <LoadingCircle size="4x" />;
                case !filteredData.length && props.error:
                  return (
                    <NoItemsFound text="There was an error fetching your data. Please contact an administrator if the problem persists." />
                  );
                case showEmptyState(filteredData, props):
                  return <NoItemsFound text="No reminders for this asset." />;
                default:
                  return width > 991 ? (
                    <div className="position-absolute h-100 w-100 overflow-auto">
                      <MaintenanceTable
                        allInstancesChecked={
                          !!filteredData.length &&
                          props.checkedInstances.size === filteredData.length
                        }
                        assetShadowSignalsMap={props.assetShadowSignalsMap}
                        checkedInstances={props.checkedInstances}
                        handleAllRowsChecked={handleAllRowsChecked}
                        handleInstanceChecked={props.handleInstanceChecked}
                        handlePropUpdate={props.handlePropUpdate}
                        handleTypeFilterSelect={props.handleTypeFilterSelect}
                        reminderNameSearch={props.reminderNameSearch}
                        rows={filteredData.slice(
                          pageLimit * props.page,
                          pageLimit * props.page + pageLimit
                        )}
                        scheduleNameSearch={props.scheduleNameSearch}
                        typeFilter={props.typeFilter}
                      />
                    </div>
                  ) : (
                    <div className="position-absolute h-100 w-100 overflow-auto">
                      {filteredData
                        ?.slice?.(
                          pageLimit * props.page,
                          pageLimit * props.page + pageLimit
                        )
                        ?.map?.((instance, i) => (
                          <div
                            className="d-lg-none m-2"
                            key={`${instance._id}-${i}`}
                          >
                            <MobileMaintenanceCard
                              assetShadowSignalsMap={
                                props.assetShadowSignalsMap
                              }
                              checkedInstances={props.checkedInstances}
                              handleMobileCardClick={
                                props.handleInstanceChecked
                              }
                              instance={instance}
                            />
                          </div>
                        )) ?? null}
                    </div>
                  );
              }
            })()}
          </div>
        </div>
        <PaginationFooter
          handlePageChange={(selectedPlusOne) =>
            props.updatePage(selectedPlusOne - 1)
          }
          page={props.page + 1}
          pages={pages}
          rowsLength={props.assetReminderInstances?.length}
          rowsLoading={props.dataLoading}
        />
        <ServiceTableMobileFilters {...props} />
        {props.showServiceCompleteModal ? (
          <ServiceCompleteModalContainer
            acknowledgementQueue={props.checkedInstances}
            assetShadowSignalsMap={props.assetShadowSignalsMap}
            isOpen={props.showServiceCompleteModal}
            refreshState={props.fetchAssetInstances}
            selectedCount={props.checkedInstances.size}
            toggleModal={() =>
              props.toggleServiceCompleteModal(!props.showServiceCompleteModal)
            }
          />
        ) : null}
        {showMobileFilters ? (
          <MobileMaintenanceFilters
            clearFilters={props.clearFilters}
            handlePropUpdate={props.handlePropUpdate}
            handleTypeFilterSelect={props.handleTypeFilterSelect}
            isOpen={showMobileFilters}
            reminderNameSearch={props.reminderNameSearch}
            scheduleNameSearch={props.scheduleNameSearch}
            toggleModal={toggleMobileFilters}
            typeFilter={props.typeFilter}
          />
        ) : null}
      </div>
    </>
  );
}

AssetMaintenanceDisplay.propTypes = {
  assetReminderInstances: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      orgId: PropTypes.string,
      orgName: PropTypes.string,
      scheduleName: PropTypes.string,
      gatewayId: PropTypes.string,
      gatewayName: PropTypes.string,
      reminder: PropTypes.shape({
        id: PropTypes.string,
        maintenance_schedule_id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
        message: PropTypes.string,
        recurring: PropTypes.bool,
        trigger: PropTypes.shape({
          type: PropTypes.oneOf(['conditional', 'calendar']),
          value: PropTypes.number,
          name: PropTypes.string,
          reminderMargin: PropTypes.number,
        }),
        attachment: PropTypes.string,
      }),
      enabled: PropTypes.bool,
      offset: PropTypes.number,
      startDate: PropTypes.string,
      endData: PropTypes.string,
      lastRun: PropTypes.string,
      pending: PropTypes.arrayOf(PropTypes.string),
      lastTriggerVal: PropTypes.number,
      preacknowledged: PropTypes.bool,
      triggerHistory: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          createdAt: PropTypes.string, // Date Trigger column value
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          due: PropTypes.bool, // true === 'due', false === 'upcoming'
          maintenanceLogId: PropTypes.string, // when present === 'completed'
          partsCost: PropTypes.string, // parts revenue column
          serviceDate: PropTypes.string, // will be present if 'completed'
        })
      ),
    })
  ),
  assetShadowSignalsMap: PropTypes.object,
  checkedInstances: PropTypes.object,
  clearFilters: PropTypes.func,
  dataLoading: PropTypes.bool,
  error: PropTypes.string,
  fetchAssetInstances: PropTypes.func,
  handleInstanceChecked: PropTypes.func,
  handlePropUpdate: PropTypes.func,
  handleSelectAllInstances: PropTypes.func,
  handleTypeFilterSelect: PropTypes.func,
  page: PropTypes.number,
  reminderNameSearch: PropTypes.string,
  scheduleNameSearch: PropTypes.string,
  showServiceCompleteModal: PropTypes.bool,
  toggleServiceCompleteModal: PropTypes.func,
  typeFilter: PropTypes.arrayOf(
    PropTypes.oneOf(['complete', 'due', 'upcoming'])
  ),
  updatePage: PropTypes.func,
};

AssetMaintenanceDisplay.defaultProps = {
  assetReminderInstances: [],
  checkedInstances: new Map(),
  typeFilter: [],
};

function showEmptyState(data, props) {
  return (
    !data.length &&
    !props.dataLoading &&
    !props.error &&
    !props.reminderNameSearch &&
    !props.scheduleNameSearch &&
    props.typeFilter.length > 3
  );
}
